import { Typography, Box, Button, Paper, Divider, Stack, Snackbar, Alert, IconButton } from '@mui/material';
import { getApiCallHistory, getLastApiCall, logLastApiCall, ApiCallLog, clearApiCallHistory } from '../utils/axios';
import { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';

export function Settings() {
  const [showApiCalls, setShowApiCalls] = useState(false);
  const [apiCalls, setApiCalls] = useState<ApiCallLog[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Refresh API call history when the component mounts
  useEffect(() => {
    const calls = getApiCallHistory();
    console.log('Initial API call history check:', calls);
    
    // Always load the calls even if not shown, for debugging
    setApiCalls(calls);
  }, []);
  
  // Refresh when showApiCalls changes
  useEffect(() => {
    if (showApiCalls) {
      const calls = getApiCallHistory();
      setApiCalls(calls);
      console.log('Current API call history:', calls);
      
      if (calls.length === 0) {
        setSnackbarMessage("No API calls have been recorded yet. Try navigating to Dashboard and interacting with it first.");
        setSnackbarOpen(true);
      }
    }
  }, [showApiCalls]);

  const handleShowLastCall = () => {
    logLastApiCall();
    const lastCall = getLastApiCall();
    if (!lastCall) {
      setSnackbarMessage("No API calls recorded yet. Try navigating to Dashboard and interacting with it first.");
      setSnackbarOpen(true);
    } else {
      // Make an API call to show it works
      console.log('Making a test API call to settings...');
      fetch('/api/settings', { method: 'GET' }).catch(() => {
        console.log('Test API call completed');
      });
    }
  };

  const handleShowAllCalls = () => {
    setShowApiCalls(!showApiCalls);
    if (!showApiCalls) {
      const calls = getApiCallHistory();
      setApiCalls(calls);
      console.log('All Recent API Calls:', calls);
      
      if (calls.length === 0) {
        setSnackbarMessage("No API calls recorded yet. Try navigating to Dashboard and interacting with it first.");
        setSnackbarOpen(true);
      }
    }
  };

  const handleRefresh = () => {
    const calls = getApiCallHistory();
    setApiCalls(calls);
    console.log('Refreshed API Calls:', calls);
    
    if (calls.length === 0) {
      setSnackbarMessage("No API calls recorded yet. Try navigating to Dashboard and interacting with it first.");
      setSnackbarOpen(true);
    } else {
      setSnackbarMessage(`Refreshed! Found ${calls.length} API calls.`);
      setSnackbarOpen(true);
    }
  };
  
  const handleClearHistory = () => {
    clearApiCallHistory();
    setApiCalls([]);
    setSnackbarMessage("API call history cleared");
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Create a test API call
  const handleTestApiCall = () => {
    console.log('Making test API call...');
    fetch('/api/test', { method: 'GET' }).catch(() => {
      console.log('Test API call completed');
      setTimeout(() => {
        handleRefresh();
      }, 500);
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Settings</Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>Debug Tools</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Tools to help with debugging API calls and filters.
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={handleShowLastCall}
          >
            Show Last API Call
          </Button>
          
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={handleShowAllCalls}
          >
            {showApiCalls ? 'Hide' : 'Show'} All Recent API Calls
          </Button>

          {showApiCalls && (
            <>
              <Button 
                variant="outlined" 
                color="info"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearHistory}
                startIcon={<DeleteIcon />}
              >
                Clear History
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleTestApiCall}
              >
                Test API Call
              </Button>
            </>
          )}
        </Stack>
        
        <Typography variant="body2" sx={{ mt: 2 }}>
          Keyboard shortcut: <strong>Ctrl+Alt+L</strong> to log the last API call to console.
          {apiCalls.length > 0 && !showApiCalls && (
            <span> You have <strong>{apiCalls.length}</strong> API calls in history. Click "Show All Recent API Calls" to view them.</span>
          )}
        </Typography>
        
        {showApiCalls && (
          <Box sx={{ mt: 3, maxHeight: '500px', overflow: 'auto' }}>
            <Typography variant="subtitle1">
              Recent API Calls: {apiCalls.length > 0 ? `(${apiCalls.length})` : '(No calls recorded)'}
            </Typography>
            
            {apiCalls.length === 0 && (
              <Paper 
                sx={{ 
                  p: 2, 
                  mt: 1, 
                  backgroundColor: '#f8f9fa',
                  border: '1px dashed',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No API calls have been recorded yet. 
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try navigating to the Dashboard and interacting with it first.
                </Typography>
                <Button 
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={handleTestApiCall}
                >
                  Make Test API Call
                </Button>
              </Paper>
            )}
            
            {apiCalls.map((call, index) => (
              <Paper 
                key={index} 
                sx={{ 
                  p: 2, 
                  mt: 1, 
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="subtitle2">
                  {call.method} {call.url}
                </Typography>
                <Typography variant="caption" display="block">
                  Time: {new Date(call.timestamp).toLocaleTimeString()} | {new Date(call.timestamp).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Query Params:</strong>
                </Typography>
                <pre style={{ 
                  fontSize: '0.8rem', 
                  background: '#f5f5f5', 
                  padding: '8px', 
                  borderRadius: '4px',
                  maxHeight: '100px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(call.params, null, 2) || "{}"}
                </pre>
                
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Request Body:</strong>
                </Typography>
                <pre style={{ 
                  fontSize: '0.8rem', 
                  background: '#f5f5f5', 
                  padding: '8px', 
                  borderRadius: '4px',
                  maxHeight: '100px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(call.data, null, 2) || "{}"}
                </pre>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
} 